import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import PortfolioItem from '../../components/portfolio/PortfolioItem';
import PortfolioForm from '../../components/portfolio/PortfolioForm';
import PortfolioModal from '../../components/portfolio/PortfolioModal';
import { 
  getPortfolioItems, 
  addPortfolioItem, 
  updatePortfolioItem, 
  deletePortfolioItem 
} from '../../services/portfolioService';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../../components/ui/select';
import { useFeedback } from '../../contexts/FeedbackContext';

// Categories for portfolio items
const PORTFOLIO_CATEGORIES = [
  'Product Review', 
  'Sponsored Post', 
  'Brand Collaboration', 
  'Video Content', 
  'Blog Post', 
  'Social Media Campaign', 
  'Other'
];

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest First' },
  { value: 'oldest', label: 'Oldest First' },
  { value: 'title', label: 'Title A-Z' },
  { value: 'category', label: 'Category' }
];

const Portfolio = () => {
  const { currentUser } = useAuth();
  const { showSuccess, showError } = useFeedback();
  const [portfolioItems, setPortfolioItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [viewItem, setViewItem] = useState(null);
  const [filterCategory, setFilterCategory] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    const fetchPortfolioItems = async () => {
      if (!currentUser) return;
      
      try {
        setLoading(true);
        setError(null);
        const items = await getPortfolioItems(currentUser.uid);
        setPortfolioItems(items);
      } catch (err) {
        setError('Failed to load portfolio items. Please check your connection and try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchPortfolioItems();
  }, [currentUser]);

  const handleAddNew = () => {
    setCurrentItem(null);
    setShowForm(true);
  };

  const handleEdit = (item) => {
    setCurrentItem(item);
    setShowForm(true);
  };

  const handleView = (item) => {
    setViewItem(item);
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this portfolio item?')) return;
    
    setDeletingId(itemId);
    try {
      const itemToDelete = portfolioItems.find(item => item.id === itemId);
      if (!itemToDelete) return;
      
      await deletePortfolioItem(
        itemId, 
        currentUser.uid, 
        itemToDelete.coverImage,
        itemToDelete.images || []
      );
      
      setPortfolioItems(portfolioItems.filter(item => item.id !== itemId));
      showSuccess('Portfolio item deleted successfully.', 'The item has been removed from your portfolio.', 3000);
    } catch (err) {
      showError('Failed to delete portfolio item.', 'Please check your connection and try again.', 5000);
    } finally {
      setDeletingId(null);
    }
  };

  const handleFormSubmit = async (formData) => {
    try {
      const { coverImageFile, additionalImageFiles, ...cleanFormData } = formData;
      
      const portfolioData = {
        ...cleanFormData,
        userId: currentUser.uid,
        createdAt: new Date()
      };
      
      if (currentItem) {
        await updatePortfolioItem(
          formData.id,
          portfolioData,
          coverImageFile,
          additionalImageFiles || []
        );
        
        setPortfolioItems(portfolioItems.map(item => 
          item.id === formData.id ? {...item, ...portfolioData} : item
        ));
        showSuccess('Portfolio item updated successfully.', 'Your changes have been saved.', 3000);
      } else {
        const newItemId = await addPortfolioItem(
          portfolioData,
          coverImageFile,
          additionalImageFiles || []
        );
        
        const newItem = {
          ...portfolioData,
          id: newItemId
        };
        
        setPortfolioItems([newItem, ...portfolioItems]);
        showSuccess('Portfolio item added successfully.', 'Your new portfolio item is now live.', 3000);
      }
      
      setShowForm(false);
      setCurrentItem(null);
    } catch (err) {
      showError('Failed to save portfolio item.', 'Please check your connection and try again.', 5000);
    }
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setCurrentItem(null);
  };

  const filteredAndSortedItems = useMemo(() => {
    const filtered = portfolioItems.filter(item => {
      const matchesCategory = filterCategory ? item.category === filterCategory : true;
      const matchesSearch = searchTerm
        ? item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.description.toLowerCase().includes(searchTerm.toLowerCase())
        : true;
      return matchesCategory && matchesSearch;
    });

    const sorted = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'oldest':
          return new Date(a.createdAt) - new Date(b.createdAt);
        case 'title':
          return a.title.localeCompare(b.title);
        case 'category':
          return (a.category || '').localeCompare(b.category || '');
        case 'newest':
        default:
          return new Date(b.createdAt) - new Date(a.createdAt);
      }
    });

    return sorted;
  }, [portfolioItems, filterCategory, searchTerm, sortBy]);

  const stats = useMemo(() => {
    const total = portfolioItems.length;
    const featured = portfolioItems.filter(item => item.featured).length;
    const categories = new Set(portfolioItems.map(item => item.category).filter(Boolean)).size;
    return { total, featured, categories };
  }, [portfolioItems]);

  if (loading) {
    return null;
  }

  if (error) {
    return (
      <div>
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-800 mb-2">Portfolio Management</h2>
          <p className="text-gray-600">Showcase your best work to attract brands and campaigns</p>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-1">Something went wrong</h3>
          <p className="text-gray-500 mb-4">{error}</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-800 mb-2">Portfolio Management</h2>
        <p className="text-gray-600">Showcase your best work to attract brands and campaigns</p>
      </div>
      
      {showForm ? (
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {currentItem ? 'Edit Portfolio Item' : 'Add New Portfolio Item'}
          </h3>
          <PortfolioForm 
            item={currentItem} 
            onSubmit={handleFormSubmit} 
            onCancel={handleFormCancel} 
          />
        </div>
      ) : (
        <>
          {/* Stats Summary */}
          {stats.total > 0 && (
            <div className="mb-6 text-sm text-gray-600">
              <span className="font-semibold text-gray-900">{stats.total}</span> item{stats.total !== 1 ? 's' : ''}
              {' • '}
              <span className="font-semibold text-gray-900">{stats.featured}</span> featured
              {' • '}
              <span className="font-semibold text-gray-900">{stats.categories}</span> categor{stats.categories !== 1 ? 'ies' : 'y'}
            </div>
          )}

          {/* Search, Filter, Sort Bar */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 space-y-4 sm:space-y-0">
            <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4 w-full sm:w-auto">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search portfolio"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                />
              </div>
              
              <Select
                value={filterCategory}
                onValueChange={(value) => setFilterCategory(value)}
              >
                <SelectTrigger className="w-full sm:w-48">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">All Categories</SelectItem>
                  {PORTFOLIO_CATEGORIES.map((category) => (
                    <SelectItem key={category} value={category}>{category}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={sortBy}
                onValueChange={(value) => setSortBy(value)}
              >
                <SelectTrigger className="w-full sm:w-44">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  {SORT_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <button
              type="button"
              onClick={handleAddNew}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark transition-colors"
            >
              Add Portfolio Item
            </button>
          </div>
          
          {/* Results Count */}
          {filteredAndSortedItems.length > 0 && (
            <div className="mb-4 text-sm text-gray-600">
              Showing {filteredAndSortedItems.length} of {portfolioItems.length} item{portfolioItems.length !== 1 ? 's' : ''}
            </div>
          )}

          {filteredAndSortedItems.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <p className="text-gray-500 mb-4">
                {searchTerm || filterCategory 
                  ? 'No portfolio items match your search criteria.' 
                  : 'You haven\'t added any portfolio items yet.'}
              </p>
              <button
                type="button"
                onClick={handleAddNew}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark transition-colors"
              >
                {searchTerm || filterCategory ? 'Add Portfolio Item' : 'Add Your First Portfolio Item'}
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {filteredAndSortedItems.map(item => (
                <PortfolioItem
                  key={item.id}
                  item={item}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onView={handleView}
                  deletingId={deletingId}
                />
              ))}
            </div>
          )}
        </>
      )}
      
      {viewItem && (
        <PortfolioModal 
          item={viewItem} 
          onClose={() => setViewItem(null)}
          onEdit={handleEdit}
        />
      )}
    </div>
  );
};

export default Portfolio;
